import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { CommonModal } from 'src/components/shared/CommonModal'
import { Button } from 'src/components/shared/Button'
import {
  INVOICE_CANCEL_CONFIRMATION_WORD,
  invoiceActionLabels,
} from 'src/constants/invoice/constants'
import { InvoiceActionVerb } from 'src/constants/invoice/enums'
import type { PendingInvoiceAction } from 'src/interfaces/invoice'
import { getInvoiceActionRun } from 'src/selectors/invoice'
import { InvoiceActionConfirmStep } from './InvoiceActionConfirmStep'
import { InvoiceActionProgressStep } from './InvoiceActionProgressStep'
import { InvoiceActionResultsStep } from './InvoiceActionResultsStep'

interface Props {
  pendingAction: PendingInvoiceAction | null
  onConfirm(invoiceIds: string[]): void
  onClose(): void
}

/**
 * Step selection comes entirely from the store:
 *
 * - no run open        → confirm
 * - run open, running  → progress
 * - run open, finished → results
 *
 * A retry re-dispatches the run, which resets `actionRun` in the reducer and moves the modal
 * back to progress without any local step state to keep in sync.
 */
const InvoiceActionModalComponent = ({ pendingAction, onConfirm, onClose }: Props) => {
  const actionRun = useSelector(getInvoiceActionRun)
  const [typedConfirmation, setTypedConfirmation] = useState<string>('')

  // Reset typed confirmation whenever a new pending action arrives, so a previous Cancel
  // confirmation cannot carry over into the next one.
  useEffect(() => {
    setTypedConfirmation('')
  }, [pendingAction])

  const requiresTypedConfirmation = pendingAction?.verb === InvoiceActionVerb.Cancel

  const isConfirmEnabled = useMemo(() => {
    if (!requiresTypedConfirmation) {
      return true
    }

    return typedConfirmation.trim().toUpperCase() === INVOICE_CANCEL_CONFIRMATION_WORD.toUpperCase()
  }, [requiresTypedConfirmation, typedConfirmation])

  const failedInvoiceIds = useMemo(() => {
    if (!actionRun || actionRun.isRunning) {
      return []
    }

    return actionRun.results
      .filter((result) => {
        return !result.ok
      })
      .map((result) => {
        return result.invoiceId
      })
  }, [actionRun])

  const handleConfirm = useCallback(() => {
    if (!pendingAction) {
      return
    }

    onConfirm(pendingAction.invoiceIds)
  }, [pendingAction, onConfirm])

  const handleRetryFailed = useCallback(() => {
    onConfirm(failedInvoiceIds)
  }, [failedInvoiceIds, onConfirm])

  // Closing mid-run would leave requests in flight with nothing reporting their outcome, so
  // the modal refuses to dismiss while the loop is running.
  const handleClose = useCallback(() => {
    if (actionRun?.isRunning) {
      return
    }

    onClose()
  }, [actionRun, onClose])

  if (!pendingAction) {
    return null
  }

  const title = actionRun
    ? `${invoiceActionLabels[pendingAction.verb]} — ${pendingAction.composedInvoiceLabel}`
    : `${invoiceActionLabels[pendingAction.verb]}?`

  let modalButtons = null

  if (!actionRun) {
    // Confirm step: show a dismiss button and the action button. When the action itself is
    // "Cancel invoices", both would otherwise read "Cancel" — an admin backing out of a typed
    // confirmation could misread the guard as the dismissal.
    modalButtons = (
      <Fragment>
        <Button size="small" color="grey" onClick={handleClose}>
          {requiresTypedConfirmation ? 'Keep invoices' : 'Cancel'}
        </Button>
        <Button
          size="small"
          color={requiresTypedConfirmation ? 'red' : 'dark'}
          disabled={!isConfirmEnabled}
          onClick={handleConfirm}
          data-testid="invoice-action-confirm-button"
        >
          {invoiceActionLabels[pendingAction.verb]}
        </Button>
      </Fragment>
    )
  } else if (actionRun.isRunning) {
    // Progress step: no actionable footer button while money is moving. Rendering `null` would
    // fall through to `CommonModal`'s default full-width black "Close" button, which no-ops via
    // `handleClose`'s early return but looks live — a disabled button makes that state honest.
    modalButtons = (
      <Button size="small" color="grey" disabled>
        Close
      </Button>
    )
  } else {
    // Results step: show Retry failed (if any failures) and Done
    modalButtons = (
      <Fragment>
        {failedInvoiceIds.length > 0 ? (
          <Button
            size="small"
            color="grey"
            onClick={handleRetryFailed}
            data-testid="invoice-action-retry-failed-button"
          >
            Retry failed
          </Button>
        ) : null}
        <Button
          size="small"
          color="primary"
          onClick={handleClose}
          data-testid="invoice-action-done-button"
        >
          Done
        </Button>
      </Fragment>
    )
  }

  return (
    <CommonModal
      isOpen
      title={title}
      onClose={handleClose}
      className={tw`max-w-[520px]`}
      buttons={modalButtons}
    >
      {!actionRun ? (
        <InvoiceActionConfirmStep
          verb={pendingAction.verb}
          composedInvoiceLabel={pendingAction.composedInvoiceLabel}
          invoiceCount={pendingAction.invoiceIds.length}
          scheduledReleaseCount={pendingAction.scheduledReleaseCount}
          typedConfirmation={typedConfirmation}
          onTypedConfirmationChange={setTypedConfirmation}
        />
      ) : null}
      {actionRun?.isRunning ? (
        <InvoiceActionProgressStep
          verb={actionRun.verb}
          completed={actionRun.completed}
          total={actionRun.total}
        />
      ) : null}
      {actionRun && !actionRun.isRunning ? (
        <InvoiceActionResultsStep results={actionRun.results} />
      ) : null}
    </CommonModal>
  )
}

export const InvoiceActionModal = memo(InvoiceActionModalComponent)
